import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import useWalletCurrency from '~/client/hooks/currency/useWalletCurrency';
import useSite from '~/client/hooks/useSite';
import { useUpdateMyDefaultCurrencyMutation } from '~/shared/graphql/UpdateMyDefaultCurrencyMutation.generated';
import {
  CurrentUserQuery,
  CurrentUserQueryDocument,
} from '~/shared/graphql/user/CurrentUserQuery.generated';
import useSportsbetUpdateCurrency from './useSportsbetUpdateCurrency';

const useSetWalletCurrency = () => {
  const { isBridgeSite, site } = useSite();
  const walletCurrency = useWalletCurrency();
  const { updateCurrency } = useSportsbetUpdateCurrency();
  const [updateMyDefaultCurrency] = useUpdateMyDefaultCurrencyMutation();

  const { cache } = useApolloClient();
  const data = cache.readQuery<CurrentUserQuery>({
    variables: { site },
    query: CurrentUserQueryDocument,
  });

  const setWalletCurrency = useCallback(
    async (currency: string) => {
      if (walletCurrency === currency) {
        return;
      }
      if (isBridgeSite) {
        await updateCurrency(currency);
      } else {
        await updateMyDefaultCurrency({
          variables: { currency },
          optimisticResponse: {
            featuresToggleCurrency: {
              features: {
                id: data?.features?.id ?? '',
                walletCurrency: currency,
                __typename: 'Features',
              },
              isTagEnabled: true,
              errors: [],
              __typename: 'FeaturesToggleCurrencyPayload',
            },
            __typename: 'Mutation',
          },
        });
      }
    },
    [
      data?.features?.id,
      isBridgeSite,
      updateCurrency,
      updateMyDefaultCurrency,
      walletCurrency,
    ],
  );

  return { setWalletCurrency };
};

export default useSetWalletCurrency;
